import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useClient} from "../../utils/hooks";
import {AxiosError} from "axios";
import {message} from "antd";
import {AnalyticsEvent} from "../../utils/analytics";
import {GetDiscountsApiResponse} from "../../discounts/useCases/get-discounts/types";
import {useGetDiscountCodesContext} from "../../discounts/useCases/get-discounts/context";
import {normalizeV2DiscountsList} from "./discount-normalizer";

export type DiscountTransaction = {
  customerName: string;
  email: string;
  offeringType: string;
  offeringName: string;
  purchaseDate: string;
  amount: number;
  currency: string;
};

type DiscountTransactionsApiRawResponse = {
  status: boolean;
  message: string;
  statusCode?: number;
  data:
    | DiscountTransaction[]
    | {
        transactions: DiscountTransaction[];
        total: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
      };
};

type DiscountTransactionsApiEnvelope = {
  data: DiscountTransactionsApiRawResponse;
};

function normalizeDiscountTransactionsResponse(
  payload: DiscountTransactionsApiRawResponse
): DiscountTransactionsResponse {
  if (Array.isArray(payload.data)) {
    return {
      transactions: payload.data,
      total: payload.data.length,
      hasNextPage: false,
      hasPrevPage: false,
    };
  }

  return {
    transactions: payload.data?.transactions ?? [],
    total: payload.data?.total ?? payload.data?.transactions?.length ?? 0,
    hasNextPage: payload.data?.hasNextPage ?? false,
    hasPrevPage: payload.data?.hasPrevPage ?? false,
  };
}

export type DiscountTransactionsResponse = {
  transactions: DiscountTransaction[];
  total: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

export const useGetDiscountCodes = (page: number = 1, per_page: number = 2) => {
  const client = useClient("DISCOUNTS");

  return useQuery<
    GetDiscountsApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["discounts", page],
    queryFn: async () => {
      const data = await client(`discounts?page=${page}&perPage=${per_page}`);
      const payload = data?.data ?? data;
      return normalizeV2DiscountsList(payload, page, per_page);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

export const useGetDiscountTransactions = (
  discountId: number,
  page: number = 1,
  perPage: number = 10
) => {
  const client = useClient("DISCOUNTS");

  return useQuery<
    DiscountTransactionsResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["discount-transactions", discountId, page, perPage],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });
      const raw = (await client(
        `discounts/${discountId}/transactions?${params.toString()}`
      )) as DiscountTransactionsApiRawResponse | DiscountTransactionsApiEnvelope;
      const payload = "status" in raw ? raw : raw.data;

      return normalizeDiscountTransactionsResponse(payload);
    },
    enabled: Number.isFinite(discountId) && discountId > 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

export const useDeleteDiscountCode = () => {
  const queryClient = useQueryClient();
  const client = useClient("DISCOUNTS");
  const {
    state: {discountCodes, currentPage},
    dispatch,
  } = useGetDiscountCodesContext();
  return useMutation({
    mutationFn: ({codeId}: {codeId: number; page: number}) =>
      client(`discounts/${codeId}`, {method: "DELETE"}),
    onMutate: ({codeId, page}) => {
      const oldDiscountCodes = queryClient.getQueryData<GetDiscountsApiResponse>([
        "discounts",
        page,
      ]);
      // console.log({oldDiscountCodes, discountCodes, page});
      let newDiscountCodes: GetDiscountsApiResponse;
      if (oldDiscountCodes) {
        newDiscountCodes = {
          data: {
            discounts: oldDiscountCodes.data.discounts.filter(
              (discountCode) => String(discountCode.id) !== String(codeId)
            ),
          },
          meta: oldDiscountCodes.meta,
        };
        queryClient.setQueryData(["discounts", page], newDiscountCodes);
      }
      if (discountCodes.length - 1 < 1) {
        dispatch({
          payload: Math.max(currentPage - 1, 1),
          type: "update-current-page",
        });
      }

      return () => {
        // console.log({roll: discountCodes, per_page, page, currentPage});
        if (page > 1) {
          // console.log({fromRollback: discountCodes, per_page});
          dispatch({
            payload: currentPage,
            type: "update-current-page",
          });
        }
        queryClient.setQueryData(["discounts", page], oldDiscountCodes);
      };
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Discounts", "Discounts delete fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      // if (discountCodes.length - 1 < 1) {
      //   dispatch({
      //     payload: Math.max(currentPage - 1, 1),
      //     type: "update-current-page",
      //   });
      // }
      AnalyticsEvent("Discounts", "Discounts delete success");
      message.success("The selected discount code has been deleted successfully!");
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["discounts"]}),
  });
};
